// DeliveriesPage — the crew's LOAD LIST (Phase 6, sql/077).
//
// What each technician has to physically put in the van for one day. This is the ONLY
// fulfilment surface for recurring deliveries: the field app is another team's product
// and does not yet show the crew a bring-list, so nobody sees this information anywhere
// else until it does. Everything on screen comes from delivery_load_list(p_date), which
// is org-scoped by the caller's JWT.
//
// The one hard rule this page exists to enforce: a drop counts as delivered ONLY when
// the field app has stamped confirmed_at. `status` is what the office typed; confirmed_at
// is what happened in the driveway.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

const HJ = () => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
});
const rpc = async (fn, body = {}) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, { method: "POST", headers: HJ(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.text()) || fn);
  return r.json();
};

// Date strings are handled as plain "YYYY-MM-DD" text, never as Date objects doing
// arithmetic: `new Date("2026-09-09")` parses as UTC midnight and renders as the 8th
// anywhere west of Greenwich, which on a load list means the crew packs the wrong day.
const shiftDay = (iso, days) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  // A malformed or missing day must not throw out of an onClick and blank the page;
  // the caller treats null as "nowhere to go" and leaves the list where it is.
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};
const fmtDay = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "";
const fmtTime = (ts) =>
  ts ? new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
// Local calendar day of a timestamp, so a confirmation can be compared against the day
// the drop was planned for without another UTC-midnight trap.
const localDay = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// "Delivered · 7:14 AM" under a Tuesday heading reads as Tuesday morning. When the app
// confirmed it on some other day, the day has to be said out loud — this screen exists
// to stop people asserting things about drops that aren't true.
const fmtConfirmed = (ts, listDate) => {
  if (!ts) return "";
  const day = localDay(ts);
  return day && listDate && day !== listDate
    ? `${new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${fmtTime(ts)}`
    : fmtTime(ts);
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ⚠️ THE RULE. confirmed_at is set by the field app's delivery_confirmed event and by
// nothing else (sql/077). status='delivered' is only ever a claim someone typed in the
// office, and a date that has passed is not evidence either — a planned drop on a past
// day with no confirmation is exactly the drop that may never have happened, so it is
// called out in red rather than quietly aging into the background.
function deliveryState(it, isPastDay) {
  if (it.confirmed_at) return { cls: "badge-green", label: "Delivered", confirmed: true };
  if (it.status === "skipped") return { cls: "badge-gray", label: "Skipped" };
  if (isPastDay) return { cls: "badge-red", label: "Not confirmed", warn: true };
  if (it.status === "loaded") return { cls: "badge-blue", label: "Loaded" };
  if (it.status === "delivered") return { cls: "badge-gold", label: "Marked delivered — not confirmed" };
  if (it.status === "planned") return { cls: "badge-gray", label: "Planned" };
  return { cls: "badge-gray", label: it.status };   // a status this build doesn't know: show it raw, don't guess
}

export function DeliveriesPage({ showToast }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);       // the last RPC payload: { date, items }
  const [date, setDate] = useState(null);       // the day being requested; null = "let the server pick today"
  const [orgToday, setOrgToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [saveErr, setSaveErr] = useState({});   // delivery_id → what went wrong, shown on the row

  // Clicking through days faster than the network answers is normal here. Without the
  // seq guard, yesterday's slow response lands after today's and the crew reads a list
  // that belongs to a different date under today's heading.
  const seq = useRef(0);
  const load = useCallback(async (d) => {
    const mine = ++seq.current;
    setLoading(true);
    setDate(d);
    try {
      const r = await rpc("delivery_load_list", d ? { p_date: d } : {});
      if (mine !== seq.current) return;
      // A payload this build can't read is a failure, not a quiet day. Without this the
      // `items` fallback below turns any shape change into "Nothing to bring", which is
      // the one wrong answer that sends a van out empty.
      if (!r || typeof r !== "object" || !r.date || !Array.isArray(r.items)) throw new Error("bad_payload");
      setData(r);
      setDate(r.date);
      setSaveErr({});
      // The org's "today" is whatever the RPC resolves in the organization's own
      // timezone, so it is only ever learned from a p_date-less call — the browser
      // clock can be a day off and would mislabel a whole list as overdue.
      if (!d) setOrgToday(r.date);
      setErr(null);
    } catch (e) {
      if (mine !== seq.current) return;
      // A failed load must not look like a quiet day. The rows are dropped rather than
      // left behind, because stale rows under a new date is the one wrong answer that
      // gets a van packed for the wrong day.
      setData(null);
      setErr(/no_org/.test(e.message || "") ? "no_org" : "load");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  // ⚠️ Without this, the whole past-day rule can switch itself off in silence. orgToday
  // is only learned from a p_date-less call, so if the FIRST load fails and the operator
  // then picks a day by hand, orgToday stays null, isPastDay is false forever, and a
  // week-old list renders every unconfirmed drop as an ordinary grey "Planned" with no
  // red banner. The org's date is re-resolved here instead of being guessed from the
  // browser clock, and if it still can't be had, the page says so rather than implying
  // everything below is fine.
  const resolving = useRef(false);
  const ensureOrgToday = useCallback(async () => {
    if (resolving.current) return;
    resolving.current = true;
    try {
      const r = await rpc("delivery_load_list", {});
      if (r && r.date) setOrgToday(r.date);
    } catch (e) {
      /* leave it unknown — the banner below tells the operator the rule is off */
    } finally {
      resolving.current = false;
    }
  }, []);
  useEffect(() => {
    if (!orgToday && data && !err) ensureOrgToday();
  }, [orgToday, data, err, ensureOrgToday]);

  const listDate = data?.date || null;
  const todayKnown = Boolean(orgToday);
  const isPastDay = Boolean(orgToday && listDate && listDate < orgToday);

  const groups = useMemo(() => {
    const items = Array.isArray(data?.items) ? data.items : [];
    const by = new Map();
    for (const it of items) {
      // A header can carry a name without an id (denormalized on job_headers), so both
      // are keys. Only a row with neither is genuinely nobody's.
      const assigned = Boolean(it.technician_id || it.technician_name);
      const key = it.technician_id || it.technician_name || "__unassigned";
      if (!by.has(key)) {
        by.set(key, {
          key,
          assigned,
          label: it.technician_name || (it.technician_id ? "Unnamed technician" : "Unassigned"),
          items: [],
        });
      }
      by.get(key).items.push(it);
    }
    const out = Array.from(by.values());
    // Unassigned first: an unassigned drop has no van to be packed into and is the one
    // that gets forgotten, so it never sits below three other groups.
    out.sort((a, b) => (a.assigned !== b.assigned ? (a.assigned ? 1 : -1) : a.label.localeCompare(b.label)));
    for (const g of out) {
      g.items.sort((x, y) => {
        const a = x.route_seq, b = y.route_seq;
        if (a == null && b == null) return String(x.client_name || "").localeCompare(String(y.client_name || ""));
        if (a == null) return 1;   // no stop number yet → last, so the rest still match the driving order
        if (b == null) return -1;
        return a - b || String(x.client_name || "").localeCompare(String(y.client_name || ""));
      });
      g.units = g.items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
      g.unconfirmed = g.items.filter((it) => deliveryState(it, isPastDay).warn).length;
    }
    return out;
  }, [data, isPastDay]);

  const totals = useMemo(() => ({
    items: groups.reduce((n, g) => n + g.items.length, 0),
    units: groups.reduce((n, g) => n + g.units, 0),
    unconfirmed: groups.reduce((n, g) => n + g.unconfirmed, 0),
    // Only assigned groups are vans. The Unassigned group is a pile on the shop floor.
    vans: groups.filter((g) => g.assigned).length,
  }), [groups]);

  const go = (days) => {
    const next = shiftDay(date || orgToday, days);
    if (next) load(next);
  };

  async function markLoaded(it) {
    const id = it.delivery_id;
    const mine = seq.current;          // the list generation this click belongs to
    setBusy((b) => { const n = new Set(b); n.add(id); return n; });
    setSaveErr((m) => (m[id] ? { ...m, [id]: null } : m));
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/deliveries?id=eq.${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { ...HJ(), Prefer: "return=representation" },
        body: JSON.stringify({ status: "loaded" }),
      });
      if (!r.ok) throw new Error((await r.text()) || "patch_failed");
      const rows = await r.json();
      // RLS does not raise on a write it forbids: deliveries_write (sql/077) is
      // owner/admin-only, so a technician's PATCH simply matches zero rows and comes
      // back 200 OK. return=representation is the only thing that tells the two apart —
      // without it the button flips and nothing was saved.
      if (!Array.isArray(rows) || rows.length === 0) throw new Error("not_permitted");
      const row = rows[0];
      showToast && showToast(`${it.client_name} — in the van.`, "success");
      // The day may have been changed while this was in flight. The write itself stands,
      // but merging its row into a list that now belongs to another date would stamp a
      // saved state onto a day it was never part of.
      if (mine !== seq.current) return;
      setData((d) => {
        if (!d || !Array.isArray(d.items)) return d;
        return {
          ...d,
          items: d.items.map((x) =>
            x.delivery_id === id ? { ...x, status: row.status, confirmed_at: row.confirmed_at } : x),
        };
      });
    } catch (e) {
      const denied = /not_permitted/.test(e.message || "") || /permission|denied|42501|policy/i.test(e.message || "");
      const msg = denied
        ? "Nothing was saved — marking a delivery loaded is limited to owners and admins. Ask one of them, or reload if this drop changed elsewhere."
        : "Couldn't save that. Check your connection and try again — the drop is still unmarked.";
      // The toast sits at the top of the document flow, so on a long load list it lands
      // above the fold and a refused write looks like it worked. Mark the row too — and
      // this is the only notice at all if the page was mounted without showToast.
      setSaveErr((m) => ({ ...m, [id]: msg }));
      showToast && showToast(msg, "error");
    } finally {
      setBusy((b) => { const n = new Set(b); n.delete(id); return n; });
    }
  }

  return (
    <div className="calm-pane">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap", paddingTop: 6 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Load list</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            What each tech puts in the van, grouped the way it gets packed
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input className="input" type="date" value={date || ""} onChange={(e) => e.target.value && load(e.target.value)}
                 style={{ maxWidth: 170 }} aria-label="Day to load for" />
          <div className="seg">
            <button onClick={() => go(-1)} disabled={!date && !orgToday} aria-label="Previous day">‹</button>
            {/* Today goes back through the p_date-less call so the org's own date is
                re-resolved rather than assumed from a session that may have crossed midnight. */}
            <button onClick={() => load(null)}>Today</button>
            <button onClick={() => go(1)} disabled={!date && !orgToday} aria-label="Next day">›</button>
          </div>
        </div>
      </div>

      {/* Standing note, not a one-off warning: until the field app puts "🎒 bring: …" on
          the stop, this screen is the bring-list and somebody has to read it at load-up. */}
      <div className="alert alert-info">
        The field app doesn't show the crew a bring-list yet, so this screen is the bring-list.
        It also can't be marked delivered from the office — a drop only counts once the app confirms it on the stop.
      </div>

      {err ? (
        <div className="card" style={{ padding: 28, textAlign: "center" }}>
          <p style={{ fontSize: 15, margin: 0 }}>
            {err === "no_org"
              ? "This login isn't attached to an operator, so there's no load list to show."
              : "Couldn't load the list — this is not “nothing to deliver”. Nothing below is missing; it simply didn't load."}
          </p>
          {err === "no_org" ? null : (
            <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => load(date)}>Try again</button>
          )}
        </div>
      ) : loading && !data ? (
        <div style={{ padding: 20, color: "var(--text2)" }}>Loading the load list…</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{fmtDay(listDate)}</div>
            {orgToday && listDate === orgToday ? <span className="badge badge-blue">Today</span> : null}
            {totals.items > 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                {plural(totals.items, "item", "items")} · {plural(totals.units, "unit", "units")}
                {totals.vans ? ` · ${plural(totals.vans, "van", "vans")}` : ""}
              </div>
            ) : null}
            {loading ? <span style={{ fontSize: 12.5, color: "var(--text3)" }}>updating…</span> : null}
          </div>

          {/* The past-day rule is off, and silence would read as "nothing is overdue". */}
          {!todayKnown ? (
            <div className="alert alert-error">
              Couldn't work out today's date for this operator, so drops the field app never confirmed
              are <strong>not</strong> being flagged below. Nothing here is evidence a drop happened —
              reload before telling a customer otherwise.
            </div>
          ) : null}

          {totals.unconfirmed > 0 ? (
            <div className="alert alert-error">
              {plural(totals.unconfirmed, "drop", "drops")} on this day {totals.unconfirmed === 1 ? "was" : "were"} never
              confirmed by the field app. That day has passed, so {totals.unconfirmed === 1 ? "it" : "they"} may not have
              been left at all — check with the tech before anyone tells the customer otherwise.
            </div>
          ) : null}

          {/* While a new day is in flight the previous day stays visible but inert, and the
              heading above it still names ITS date — a greyed-out correct list beats a flash
              of empty that reads as "no deliveries". */}
          <div style={{ opacity: loading ? 0.45 : 1, pointerEvents: loading ? "none" : undefined, transition: "opacity .15s" }}>
            {groups.length === 0 ? (
              <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--text2)" }}>
                Nothing to bring on {fmtDay(listDate)}. No deliveries are planned for that day.
              </div>
            ) : groups.map((g) => (
              <div key={g.key} className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
                <div style={{
                  display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10,
                  flexWrap: "wrap", padding: "12px 8px 10px", borderBottom: "1px solid var(--border)",
                }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {g.label}
                    {!g.assigned ? (
                      <span className="badge badge-gold" style={{ marginLeft: 8 }}>No tech on the stop</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                    {plural(g.items.length, "item", "items")} · {plural(g.units, "unit", "units")}
                    {g.unconfirmed ? <span style={{ color: "var(--danger)" }}> · {g.unconfirmed} not confirmed</span> : null}
                  </div>
                </div>

                {g.items.map((it) => {
                  const st = deliveryState(it, isPastDay);
                  const when = st.confirmed ? fmtConfirmed(it.confirmed_at, listDate) : "";
                  const where = [it.address, [it.city, it.zip].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
                  const saving = busy.has(it.delivery_id);
                  const rowErr = saveErr[it.delivery_id];
                  // Only offer the office a button on a day that hasn't happened yet. On a past
                  // day the drop is already either confirmed or suspect, and typing "loaded"
                  // onto it manufactures progress the field app never reported. If today can't
                  // be resolved at all, we don't know which side of the line this day is on.
                  const canMark = todayKnown && !isPastDay && it.status === "planned" && !it.confirmed_at;
                  return (
                    <div key={it.delivery_id} className="alist-row"
                         style={{ cursor: "default", alignItems: isMobile ? "flex-start" : "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
                      <div style={{ width: 26, flexShrink: 0, textAlign: "center", fontSize: 12.5, color: "var(--text3)", fontVariantNumeric: "tabular-nums" }}>
                        {it.route_seq != null ? it.route_seq : ""}
                      </div>
                      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{it.client_name}</div>
                        {where ? <div style={{ fontSize: 12.5, color: "var(--text2)" }}>{where}</div> : null}
                        {it.product ? (
                          <div style={{ fontSize: 14, marginTop: 3 }}>
                            <strong style={{ fontVariantNumeric: "tabular-nums" }}>{it.quantity}×</strong> {it.product}
                          </div>
                        ) : null}
                        {st.warn && it.status !== "planned" ? (
                          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
                            Marked {it.status} in the CRM, but the app never confirmed it.
                          </div>
                        ) : null}
                        {rowErr ? (
                          <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 3 }}>{rowErr}</div>
                        ) : null}
                      </div>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
                        marginTop: isMobile ? 8 : 0, marginLeft: isMobile ? 26 : 0,
                        width: isMobile ? "100%" : undefined,
                      }}>
                        <span className={`badge ${st.cls}`}>{st.label}{when ? ` · ${when}` : ""}</span>
                        {canMark ? (
                          <button className="btn btn-ghost" disabled={saving} onClick={() => markLoaded(it)}>
                            {saving ? "Saving…" : "Mark loaded"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
